import { httpClient } from '@/shared/api/httpClient';

export type ProdutoInfo = {
  nome: string;
  descricao: string | null;
  precoBRL: number;
  precoOriginalBRL: number | null;
  frequenciaMeses: number;
};

export type EntitlementPosVenda = {
  ativo: boolean;
  validaAte: string | null;
  verificadoEm: string;
  origem: 'servidor' | 'cache' | 'sem-conexao';
  produto: ProdutoInfo | null;
};

export type PersonalizacaoCliente = { clienteId: string; texto: string };
export type GatilhoMensagem = {
  ativo: boolean;
  texto: string;
  personalizacoes?: PersonalizacaoCliente[];
};
export type GatilhoAgendado = { ativo: boolean; texto: string; dias: number };
export type Agendados = {
  aniversario: GatilhoMensagem;
  manutencao: GatilhoAgendado;
  ausencia: GatilhoAgendado;
  avaliacao: GatilhoAgendado;
};
export type ConfigPosVenda = {
  linkPadrao: string | null;
  gatilhosStatus: Record<string, GatilhoMensagem>;
  gatilhoCriacao: GatilhoMensagem;
  agendados: Agendados;
};

export type StatusMensagem = 'nao_enviada' | 'enviada' | 'falhou';

export type MensagemPosVenda = {
  id: string;
  tipo: 'os' | 'manual';
  orcamentoId: string | null;
  orcamentoNumero: string | null;
  clienteNome: string | null;
  numero: string | null;
  texto: string;
  status: StatusMensagem;
  /** Motivo da falha, como veio do servidor. */
  erro: string | null;
  tentativas: number;
  criadoEm: string;
  enviadoEm: string | null;
};

export type ListaMensagens = {
  mensagens: MensagemPosVenda[];
  contagem: Record<StatusMensagem, number>;
};

export type EstadoInstancia = {
  status: 'desconectada' | 'conectando' | 'conectada';
  numero: string | null;
  qrcode: string | null;
};

export const posVendaApi = {
  /** Verifica o direito de uso no servidor. `forcar` ignora o cache de 60s. */
  entitlement: (forcar = false) =>
    httpClient.get<EntitlementPosVenda>(
      `/api/pos-venda/entitlement${forcar ? '?forcar=true' : ''}`,
    ),
  obterConfig: () => httpClient.get<ConfigPosVenda>('/api/pos-venda/config'),
  salvarConfig: (dados: ConfigPosVenda) =>
    httpClient.put<ConfigPosVenda>('/api/pos-venda/config', dados),
  /**
   * `forcar` ignora o cache de status do servidor e consulta a uazapi na
   * hora. A tela de conexão usa isso enquanto o QR está visível.
   */
  statusWhatsapp: (forcar = false) =>
    httpClient.get<EstadoInstancia>(
      `/api/pos-venda/instancia${forcar ? '?forcar=true' : ''}`,
    ),
  conectarWhatsapp: () => httpClient.post<EstadoInstancia>('/api/pos-venda/instancia/conectar', {}),
  /** Encerra a sessão atual — usado antes de conectar outro número. */
  desconectarWhatsapp: () =>
    httpClient.post<EstadoInstancia>('/api/pos-venda/instancia/desconectar', {}),
  listarMensagens: (status: StatusMensagem | 'todas' = 'todas') =>
    httpClient.get<ListaMensagens>(`/api/pos-venda/mensagens?status=${status}`),
  reenviarMensagem: (id: string) =>
    httpClient.post<MensagemPosVenda>(`/api/pos-venda/mensagens/${id}/reenviar`, {}),
  reenviarPendentes: () =>
    httpClient.post<{ tentadas: number; enviadas: number; falharam: number }>(
      '/api/pos-venda/mensagens/reenviar-pendentes',
      {},
    ),
  excluirMensagem: (id: string) => httpClient.delete<void>(`/api/pos-venda/mensagens/${id}`),

  campanhaPrevia: (filtro: FiltroCampanha) =>
    httpClient.post<{ total: number; amostra: string[] }>('/api/pos-venda/campanha/previa', { filtro }),
  campanhaEnviar: (filtro: FiltroCampanha, texto: string) =>
    httpClient.post<{ id: string; total: number }>('/api/pos-venda/campanha', { filtro, texto }),
  campanhaStatus: (id: string) => httpClient.get<CampanhaStatus>(`/api/pos-venda/campanha/${id}`),

  /** Envio manual "do balcão": dispara a mensagem da OS para o cliente na hora. */
  enviarOS: (orcamentoId: string, texto: string) =>
    httpClient.post<{ ok: boolean; mensagem: string }>('/api/pos-venda/enviar-os', { orcamentoId, texto }),

  optOutListar: () => httpClient.get<OptOut[]>('/api/pos-venda/optout'),
  optOutAdicionar: (numero: string) => httpClient.post<OptOut[]>('/api/pos-venda/optout', { numero }),
  optOutRemover: (numero: string) =>
    httpClient.delete<OptOut[]>(`/api/pos-venda/optout?numero=${encodeURIComponent(numero)}`),
};

export type FiltroCampanha = {
  tipo: 'todos' | 'marca' | 'aniversariantesMes' | 'semVoltarDias' | 'ultimaVendaPeriodo';
  marca?: string;
  dias?: number;
  de?: string;
  ate?: string;
};

export type CampanhaStatus = {
  id: string;
  status: 'processando' | 'concluida' | 'falha';
  total: number;
  enviados: number;
  falhas: number;
  ignorados: number;
  criadoEm: string;
  concluidoEm: string | null;
};

export type OptOut = { numero: string; origem: string; criadoEm: string };
